import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  tmsApi,
  type CreateOrderTaskRequest,
  type OrderTask,
  type ResequenceOrderTasksRequest,
  type TaskStateValue,
  type UpdateOrderTaskRequest,
} from '../client';

/**
 * Stable query-key factory for order-task caches. Keyed by
 * `'tms' + 'orderTask'` so it never collides with the order or
 * booking caches even though they all share the same QueryClient.
 */
export const orderTaskKeys = {
  all: ['tms', 'orderTask'] as const,
  byOrder: (orderId: string) => ['tms', 'orderTask', 'order', orderId] as const,
  byId: (id: string) => ['tms', 'orderTask', 'id', id] as const,
};

/**
 * Fetch every task for an order (with cargo items embedded). Pass
 * `null` to keep the query disabled (e.g. while the order id is
 * still resolving from the route).
 */
export function useOrderTasks(
  orderId: string | null,
  options?: Omit<UseQueryOptions<OrderTask[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderTask[], Error>({
    queryKey: orderId ? orderTaskKeys.byOrder(orderId) : orderTaskKeys.all,
    queryFn: () => {
      if (!orderId) throw new Error('No order id');
      return tmsApi.getOrderTasks(orderId);
    },
    enabled: !!orderId,
    staleTime: 30 * 1000,
    ...options,
  });
}

/** Fetch a single task by id, with cargo items embedded. */
export function useOrderTask(
  id: string | null,
  options?: Omit<UseQueryOptions<OrderTask, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<OrderTask, Error>({
    queryKey: id ? orderTaskKeys.byId(id) : orderTaskKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No task id');
      return tmsApi.getOrderTask(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * POST a new task. BE rejects booking-half task types — those come
 * through `useCreateOrderBooking` so the pair invariant is preserved.
 * Invalidates the per-order task list cache so the Tasks panel
 * picks up the new row.
 */
export function useCreateOrderTask() {
  const qc = useQueryClient();
  return useMutation<OrderTask, Error, CreateOrderTaskRequest>({
    mutationKey: [...orderTaskKeys.all, 'create'],
    mutationFn: (body) => tmsApi.createOrderTask(body),
    onSuccess: (_task, vars) => {
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(vars.orderId) });
    },
  });
}

/**
 * PUT a task. The caller passes `orderId` alongside the body so we
 * can invalidate the per-order list cache without an extra round-
 * trip — the wire body itself only carries the task id.
 */
export function useUpdateOrderTask() {
  const qc = useQueryClient();
  return useMutation<OrderTask, Error, { orderId: string; body: UpdateOrderTaskRequest }>({
    mutationKey: [...orderTaskKeys.all, 'update'],
    mutationFn: ({ body }) => tmsApi.updateOrderTask(body),
    onSuccess: (task, { orderId }) => {
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(orderId) });
      qc.setQueryData(orderTaskKeys.byId(task.id), task);
    },
  });
}

/**
 * Move one leg along — Start, Complete, or Cancel.
 *
 * <p>
 * Invalidates the order's task list rather than patching it: completing a leg
 * can stamp actuals server-side, and the workflow's "all legs completed" rule
 * reads the same list, so the screen should see what was actually written
 * rather than what was asked for.
 * </p>
 */
export function useSetOrderTaskState() {
  const qc = useQueryClient();
  return useMutation<OrderTask, Error, { orderId: string; taskId: string; state: TaskStateValue }>({
    mutationKey: [...orderTaskKeys.all, 'set-state'],
    mutationFn: ({ taskId, state }) => tmsApi.setOrderTaskState(taskId, state),
    onSuccess: (task, { orderId }) => {
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(orderId) });
      qc.setQueryData(orderTaskKeys.byId(task.id), task);
    },
  });
}

/**
 * PUT a whole new task ordering for one order, atomically.
 *
 * <p>
 * Optimistic: a drag has to land under the operator's cursor
 * immediately, so the cached list is reordered up front and rolled
 * back if the write fails. The success path seeds the cache straight
 * from the response (which the BE already returns in the new order,
 * cargo items included) rather than invalidating — dragging several
 * rows in a row would otherwise fire a refetch per drop.
 * </p>
 */
export function useResequenceOrderTasks() {
  const qc = useQueryClient();
  return useMutation<
    OrderTask[],
    Error,
    ResequenceOrderTasksRequest,
    { previous: OrderTask[] | undefined }
  >({
    mutationKey: [...orderTaskKeys.all, 'resequence'],
    mutationFn: (body) => tmsApi.resequenceOrderTasks(body),
    onMutate: async (body) => {
      const key = orderTaskKeys.byOrder(body.orderId);
      // An in-flight GET would land after the optimistic write and
      // reinstate the old order, so stop it before touching the cache.
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<OrderTask[]>(key);
      if (previous) {
        const nextSequence = new Map(body.items.map((i) => [i.taskId, i.sequence]));
        qc.setQueryData<OrderTask[]>(
          key,
          previous
            .map((t) => ({ ...t, sequence: nextSequence.get(t.id) ?? t.sequence }))
            .sort((a, b) => a.sequence - b.sequence),
        );
      }
      return { previous };
    },
    onError: (_err, body, context) => {
      const key = orderTaskKeys.byOrder(body.orderId);
      if (context?.previous) qc.setQueryData(key, context.previous);
      // Resync regardless — the rollback restores what we last saw,
      // which isn't necessarily what the server now holds.
      qc.invalidateQueries({ queryKey: key });
    },
    onSuccess: (tasks, body) => {
      qc.setQueryData(orderTaskKeys.byOrder(body.orderId), tasks);
      for (const t of tasks) qc.setQueryData(orderTaskKeys.byId(t.id), t);
    },
  });
}

/**
 * DELETE a task by id. BE returns 409 Conflict if the task is a
 * booking half — the FE should hide the trash for those rows so
 * the operator deletes the booking instead.
 */
export function useDeleteOrderTask() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { orderId: string; taskId: string }>({
    mutationKey: [...orderTaskKeys.all, 'delete'],
    mutationFn: ({ taskId }) => tmsApi.deleteOrderTask(taskId),
    onSuccess: (_res, { orderId, taskId }) => {
      qc.invalidateQueries({ queryKey: orderTaskKeys.byOrder(orderId) });
      qc.removeQueries({ queryKey: orderTaskKeys.byId(taskId) });
    },
  });
}
